import {
	ShareBaseFolder,
	ShareBaseLibrary,
	ShareBaseDocument,
	ShareBaseItem,
	pxItemTypeGuards as typeGuard,
} from 'phoenix/domain';
import { IFolderService } from 'sharebase/services/folderService';
import { FolderDataStore } from '../../dataStores/folderDataStore';
import { UploadService } from 'phoenix/services/uploadService';
import { IFolderUploadService } from 'sharebase/services/folderUploadService';
import { SelectionDataStore } from 'sharebase-app/dataStores/selectionDataStore';
import { ActionService } from 'phoenix/services/actionService';
import { AppAction } from 'sharebase-app/components/_app/app.actions';
import { TrackingService } from 'sharebase/services';

require('./dropUploadTarget.styles');
/**
 * @ngdoc directive
 * @name sharebase-app.directive:DropUploadTargetDirective
 * @module sharebase-app
 * @restrict E
 * @description
 *
 * `DropUploadTargetDirective` enables element to receive a drop for uploading
 *
 * @usage
 *
 * <sba-element sba-drop-upload-target />
 *
 */
export function DropUploadTargetDirective(
	$q:ng.IQService,  
	$state,
	pxUploadService:UploadService,
	pxActionService:ActionService,
	sbToastService, 
	sbFolderService:IFolderService, 
	sbLibraryService, 
	sbFolderUploadService:IFolderUploadService,
	sbTrackingService: TrackingService,
	sbaSelectionDataStore: SelectionDataStore,
	sbaFolderDataStore: FolderDataStore,
	sbaLibraryDataStore,
	sbaInProgressItemsDataStore,
	gettextCatalog:ng.gettext.gettextCatalog
) {
	
	return {
		restrict: 'A',
		scope: {
			onUpload: '<?',
			onUploadError: '<?'
		},
		link: function (scope, element, attrs) {

			const enabled = () => {
				let val = true;
				let attr = attrs['sbaDropUploadEnabled'];
				if (attr === false || attr === 'false') {
					val = false;
				}

				return val;
			};

			element.addClass('sba-drop-upload-target');
			element.on('dragend dragover dragenter dragleave drop', function(e) {
				e.preventDefault();
				e.stopPropagation();
			})
			.on('dragenter', function(e:DragEvent) {
				e.dataTransfer.dropEffect = "move";
				angular.element(document.querySelectorAll('.sba-drop-upload-target---active')).removeClass('sba-drop-upload-target---active');

				let target = e.currentTarget as Node;
				let parentDropTarget = angular.element((target.parentNode as Element).closest('.sba-drop-upload-target'));
				if (parentDropTarget.length === 1) {
					parentDropTarget.triggerHandler('dragenterchild');
				}

				let libraryRegex = /^(\d+)$/;
				let libraryMatch = libraryRegex.exec(attrs.sbaDropUploadTarget);
				let uploadDisabled = angular.element(element).hasClass('sba-drop-upload-disabled');
				let isWrapper = attrs.sbaDropUploadWrapper !== undefined;
				
				if (!enabled()) {
					e.dataTransfer.effectAllowed = 'none';
					e.dataTransfer.dropEffect = 'none';
					e.preventDefault();
				} else if (libraryMatch || uploadDisabled || isWrapper){
					if (document.querySelectorAll("[drag-to-move=true]").length === 0){
						angular.element(document.querySelector('.sba-drop-upload-wrapper')).addClass('sba-dragging-inside sba-drop-upload-target---active');
					}
				} else if (!uploadDisabled){
					element.addClass('sba-drop-upload-target---active');
					angular.element(document.querySelector('.sba-drop-upload-wrapper')).addClass('sba-dragging-inside');
				}
			})
			.on('drop', (e:DragEvent) => {
				
				pxActionService.emit({type: AppAction.EndDrag});
				// List of elements being dragged. Only used as an 'is it there' kind of
				// variable. Won't actually be using for item info, that is the selection
				// data store's job.
				let draggingFileStr = e.dataTransfer.getData('text');
				let uploadDisabled = angular.element(element).hasClass('sba-drop-upload-disabled');

				let selectedFiles: ShareBaseItem[] = sbaSelectionDataStore.selectedItems;

				let urlUpload = attrs.sbaDropUploadTarget;
				// This regex will match when dropping on a folder. If this is a match
				// then new documents are adding.
				let regex = /(\/f\/)(.*)(\/documents)/;
				let newDocMatch = regex.exec(urlUpload);
				// This regex will match when dropping on a document. If this is a match,
				// then the user is replacing the document, i.e. uploading a new revision.
				// If this is the case, only let the user upload one file.
				let revRegex = /\/documents\/(\d+)\/revisions/;
				let replaceDocMatch = revRegex.exec(urlUpload);

				let libraryRegex = /^(\d+)$/;
				let libraryMatch = libraryRegex.exec(urlUpload);

				let files = e.dataTransfer.files;
				
				let useFSApi = newDocMatch || libraryMatch;

				// An optional parameter that can be used to restrict the file type.
				// This will be used on document revisions since the API enforces revisions
				// to be the same file type as the original.
				let expectedFileType = attrs.sbaDropUploadTargetAccept;
				
				element.removeClass('sba-drop-upload-target---active');
				let target = e.currentTarget as Node;
				let parentDropTarget = angular.element((target.parentNode as Element).closest('.sba-drop-upload-target'));
				if (parentDropTarget.length === 1) {
					parentDropTarget.triggerHandler('droppedonchild');
				}

				angular.element((e.target as Element).closest('.sba---is-dragging')).removeClass('sba---is-dragging');

				let uploadSuccessCallback = scope.onUpload ? scope.onUpload : angular.noop;
				let uploadErrorCallback = scope.onUploadError ? scope.onUploadError : angular.noop;

				// Errors will occur if user attempts to drop multiple files for replacing
				// a document, plus we cannot guarantee the order of the revisions match
				// the user's intention so block if replacing and there a multiple files
				
				sbTrackingService.trackClick('UPLOAD_DOCUMENT', 'Drag and Drop');

				if (replaceDocMatch && files.length > 1) {
					sbToastService.showSimple(gettextCatalog.getString('Multiple files cannot be dropped'));
				} else if (useFSApi && sbFolderUploadService.fileSystemIsSupported(e.dataTransfer) && !draggingFileStr) {
					let items: Entry[] = [];
					for (let i = 0; i < e.dataTransfer.items.length; i++) {
						items.push(e.dataTransfer.items[i].webkitGetAsEntry());
					}

					let p: ng.IPromise<ShareBaseFolder | ShareBaseLibrary>;
					if (libraryMatch) {
						let id = parseInt(libraryMatch[0], 10);
						p = sbLibraryService.getLibraries()
							.then((libraries: ShareBaseLibrary[]) => libraries.filter(l => l.LibraryId === id)[0])
							.then((library) => {
								library.IsLibrary = true;
								return library;
							});
					} else {
						let folderId = parseInt(newDocMatch[2], 10);
						p = sbFolderService.getFolder(folderId);
					}
					
					p.then((parent) => sbFolderUploadService.uploadEntries(items, parent))
					.then((result) => {
						if (result.errors) {
							result.errors.forEach((err) => sbToastService.showSimple(err));
						}
					});

				} else if (useFSApi && draggingFileStr) {
					let p: ng.IPromise<ShareBaseFolder | ShareBaseLibrary>;
					if (libraryMatch) {
						let id = parseInt(libraryMatch[0], 10);
						p = sbLibraryService.getLibraries()
							.then((libraries: ShareBaseLibrary[]) => libraries.filter(l => l.LibraryId === id)[0])
							.then((library) => {
								library.IsLibrary = true;
								return library;
							});
					} else {
						let folderId = parseInt(newDocMatch[2], 10);
						p = sbFolderService.getFolder(folderId);
					}

					let draggingFile = JSON.parse(draggingFileStr);
					// If this runs, then selected files are being moved to a new location from
					// within the application. Check to see if the file location is being moved.
					// DraggableFile Directive adds the sba-drop-upload-disabled class.
					if (!attrs.sbaDropUploadWrapper && !libraryMatch && !uploadDisabled){
						// The file is being moved.
						let draggables: ShareBaseItem[] = [draggingFile];
						let docsToMove: ShareBaseDocument[] = [], foldersToMove: ShareBaseFolder[] = [];
						let item: any = {};
						if (selectedFiles.length > 1){
							draggables = selectedFiles;
						}
						draggables.forEach(dragItem => {
							if (typeGuard.isShareBaseDocument(dragItem)) {
								docsToMove = docsToMove.concat(dragItem);
							} else if (typeGuard.isShareBaseFolder(dragItem)) {
								foldersToMove = foldersToMove.concat(dragItem);
							} 
						});

						let promises:ng.IPromise<any>[] = [];

						p.then(destination => {
							if(typeGuard.isShareBaseFolder(destination)){
								promises.push(sbaFolderDataStore.moveDocument(docsToMove, destination));
							}
							if ($state.current.name.indexOf('content.myFiles.home') >= 0){
								promises.push(sbaLibraryDataStore.moveFolder(foldersToMove, destination));
							} else {
								promises.push(sbaFolderDataStore.moveFolder(foldersToMove, destination));
							}
							sbaInProgressItemsDataStore.setItems((docsToMove as ShareBaseItem[]).concat(foldersToMove));
							$q.all(promises)
								.then(() => sbToastService.showSimple(gettextCatalog.getString('Your items have been moved')))
								.catch((err) => {
									if (err.data.StatusCode === 'ERR_DOCUMENT_ALREADY_EXISTS'){
										sbToastService.showSimple(gettextCatalog.getString(err.data.Message + ' already exists in destination.'));
									} else if (err.status === 403){
										sbToastService.showSimple(gettextCatalog.getString('You do not have rights to move these items.'));
									} else {
										sbToastService.showSimple(gettextCatalog.getString('Unable to move your items'));
									}
								})
								.finally(() => {
									sbaInProgressItemsDataStore.clear();
									sbaSelectionDataStore.clearSelection();
								});
						}).catch(err => {
							if (err.status === 404){
								sbToastService.showSimple(gettextCatalog.getString('Destination folder not found.'));
							} else {
								sbToastService.showSimple(gettextCatalog.getString('Error getting folder.'));
							}
						});
					}
				} 
				else {
					// If the app reaches this point, the current browser does not support drag n drop folders.
					if (files.length > 0) {
						for (let i = 0; i < files.length; i++) {
							let file = files.item(i);
							if (newDocMatch) {
								pxUploadService.addFile(file, parseInt(newDocMatch[2], 10), file.name, urlUpload)
									.then(() => uploadSuccessCallback())
									.catch((err) => uploadErrorCallback(err));
							} 
							else if (replaceDocMatch) {
								let fileNameParts = file.name.split('.');
								let docId = parseInt(replaceDocMatch[1], 10);

								// Only try to replace the document if no expected file type was provided or if one was
								// provided and the dropped file matches the expected file type.
								if (docId !== NaN && (expectedFileType && expectedFileType === fileNameParts[fileNameParts.length - 1]) || !expectedFileType) {
									pxUploadService.replaceFile(file, docId, attrs.sbaDropUploadTargetName, urlUpload)
										.then(() => uploadSuccessCallback())
										.catch((err) => uploadErrorCallback(err));
								} 
								else {
									sbToastService.showSimple(gettextCatalog.getString('Upload Error: File type mismatch'));
								}
							}
						}
					} 
					else {
						// Should only hit this point if current browser does not support drag n drop and user attempted to drop
						// only folders
						sbToastService.showSimple(gettextCatalog.getString('Uploading folders is not supported in this browser'));
					}
				}
			});
		}
	}
};

DropUploadTargetDirective.$inject =  [
	'$q',
	'$state',
	'pxUploadService',
	'pxActionService',
	'sbToastService',
	'sbFolderService',
	'sbLibraryService',
	'sbFolderUploadService',
	'sbTrackingService',
	'sbaSelectionDataStore',
	'sbaFolderDataStore',
	'sbaLibraryDataStore',
	'sbaInProgressItemsDataStore',
	'gettextCatalog'
];


// WEBPACK FOOTER //
// ./src/sharebase-app/components/dropUploadTarget/dropUploadTarget.ts